'use client';

import { Button, Typography } from 'lighthouse';

import { SafeImage } from '@/widgets/SafeImage';

import { withSeparator } from '@/utilities/withSeparator';

import { CAMPAIGN_DISCOUNT_AMOUNT } from '@/constants/batteryToBattery';

import classes from '../../index.module.css';

import { BatteryCharging, Flash, Location, ShieldTick, TicketDiscount, Timer1 } from 'iconsax-react';

interface HeroProps {
    onCtaClick: () => void;
    onProductsClick: () => void;
}

const trustItems = [
    { id: 1, icon: <Flash size={16} color='#FBBF24' />, label: '۱۰۰٪ رایگان، بدون هزینه امداد' },
    { id: 2, icon: <Location size={16} color='#FBBF24' />, label: 'در محل؛ خونه، محل کار یا کنار خیابون' },
    { id: 3, icon: <Timer1 size={16} color='#FBBF24' />, label: 'اعزام فوری کارشناس در تهران' }
];

const Hero = ({ onCtaClick, onProductsClick }: HeroProps) => {
    return (
        <section className={classes.hero}>
            <div aria-hidden className={`${classes.heroAmbient} ${classes.heroAmbientPrimary}`} />
            <div aria-hidden className={`${classes.heroAmbient} ${classes.heroAmbientAmber}`} />
            <div aria-hidden className={`absolute inset-0 ${classes.heroDots}`} />

            <div
                className='relative mx-auto flex w-full max-w-6xl flex-col items-center gap-12 px-5 pb-16 pt-14 lg:flex-row lg:items-center
                    lg:justify-between lg:gap-8 lg:pb-24 lg:pt-20'>
                <div className='flex max-w-xl flex-col items-center text-center lg:items-start lg:text-right'>
                    <span
                        className={`${classes.entrance} flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3.5 py-1.5 ring-1 ring-amber-300/40`}>
                        <Flash size={16} color='#FBBF24' />
                        <Typography variant='caption' component='span' className='font-bold text-amber-300'>
                            پیشنهاد محدود ازکی‌سرویس
                        </Typography>
                    </span>

                    <Typography
                        variant='h1'
                        component='h1'
                        className={`${classes.entrance} ${classes.entranceDelay1} mt-5 text-4xl font-black leading-[1.3] text-white sm:text-5xl
                            lg:text-[3.4rem]`}>
                        ماشینت روشن نمی‌شه؟{' '}
                        <span className='relative mt-4 inline-block whitespace-nowrap text-amber-300'>
                            باتری به باتری رایگان
                            <svg
                                aria-hidden
                                className='absolute -bottom-7 right-0 w-full'
                                viewBox='0 0 120 12'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                preserveAspectRatio='none'>
                                <path d='M2 9C30 3 70 3 118 8' stroke='#FBBF24' strokeWidth='4' strokeLinecap='round' opacity='0.6' />
                            </svg>
                        </span>
                    </Typography>

                    <Typography
                        variant='body1'
                        component='p'
                        className={`${classes.entrance} ${classes.entranceDelay2} mt-6 max-w-md leading-8 text-white/70`}>
                        کارشناس ازکی‌سرویس هرجا باشی میاد و ماشینت رو <span className='font-bold text-white'>کاملاً رایگان</span> روشن می‌کنه؛ بدون
                        هزینه امداد و جابه‌جایی. فقط با یک ثبت‌نام، {withSeparator(CAMPAIGN_DISCOUNT_AMOUNT)} تومان تخفیف خرید باتری و لوازم یدکی جدید
                        هم می‌گیری.
                    </Typography>

                    <div
                        className={`${classes.entrance} ${classes.entranceDelay3} mt-8 flex w-full flex-col items-center gap-3 sm:flex-row
                            sm:justify-center lg:justify-start`}>
                        <Button
                            size='large'
                            variant='contained'
                            onClick={onCtaClick}
                            endIcon={<TicketDiscount size={20} color='currentcolor' />}
                            className={`w-full bg-amber-400 font-extrabold text-[#0A1530] hover:bg-amber-300 sm:w-auto ${classes.glowPulse}`}>
                            دریافت باتری به باتری رایگان
                        </Button>
                        <Button
                            size='large'
                            variant='outlined'
                            onClick={onProductsClick}
                            className='w-full border-white/30 text-white hover:border-white/60 sm:w-auto'>
                            مشاهده باتری‌ها با تخفیف
                        </Button>
                    </div>

                    <div
                        className={`${classes.entrance} ${classes.entranceDelay4} mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2
                            lg:justify-start`}>
                        {trustItems.map((item) => (
                            <span key={item.id} className='flex items-center gap-1.5 text-xs text-white/60'>
                                {item.icon}
                                {item.label}
                            </span>
                        ))}
                    </div>
                </div>

                <div className={`${classes.entrance} ${classes.entranceDelay2} relative w-full max-w-sm lg:max-w-md`}>
                    <div aria-hidden className={`absolute -inset-10 rounded-full opacity-50 ${classes.blobPrimary}`} />

                    <div className='relative rounded-lg bg-white/[0.06] p-6 ring-1 ring-white/15 backdrop-blur-md sm:p-8'>
                        <div className='flex items-center justify-between'>
                            <span
                                className='flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-medium text-white/80 ring-1
                                    ring-white/10'>
                                <BatteryCharging size={16} color='#FBBF24' />
                                امداد باتری رایگان در تهران
                            </span>
                            <span aria-hidden className='flex h-8 items-end gap-1' dir='ltr'>
                                {[0, 1, 2, 3].map((bar) => (
                                    <span
                                        key={bar}
                                        className={`w-2 rounded-lg bg-amber-300 ${classes.chargeBar}`}
                                        style={{ height: `${10 + bar * 6}px`, animationDelay: `${bar * 0.35}s` }}
                                    />
                                ))}
                            </span>
                        </div>

                        <div className='relative mx-auto my-6 h-44 w-44 sm:h-52 sm:w-52'>
                            <SafeImage
                                width={208}
                                height={208}
                                src='/images/battery/varian-plus-50ahl1.webp'
                                alt='باتری خودرو با گارانتی - خرید باتری ماشین از ازکی‌سرویس'
                                fallback='/images/battery/battery-placeholder.webp'
                                className='h-full w-full object-contain drop-shadow-[0_24px_40px_rgba(0,143,255,0.35)]'
                            />
                        </div>

                        <div className='flex items-center justify-center gap-2 rounded-lg bg-[#050B1E]/60 px-4 py-3 ring-1 ring-white/10'>
                            <ShieldTick size={20} color='#FBBF24' />
                            <Typography variant='body2' component='p' className='text-white/90'>
                                باتری به باتری در محل، <span className='font-extrabold text-amber-300'>بدون یک تومان هزینه</span>
                            </Typography>
                        </div>
                    </div>

                    <div aria-hidden className={`absolute -right-2 top-4 sm:-right-6 ${classes.floatSlow}`}>
                        <span
                            className='flex items-center gap-1.5 rounded-lg bg-amber-400 px-3.5 py-2 text-sm font-black text-[#0A1530]
                                shadow-elevation2'>
                            <Flash size={18} color='currentcolor' />
                            رایگان
                        </span>
                    </div>
                    <div aria-hidden className={`absolute -left-2 bottom-28 sm:-left-6 ${classes.floatSlower}`}>
                        <span
                            className='flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-xs font-extrabold text-[#0A1530]
                                shadow-elevation2'>
                            <TicketDiscount size={18} color='#008fff' />
                            {withSeparator(CAMPAIGN_DISCOUNT_AMOUNT)} تومان تخفیف خرید
                        </span>
                    </div>
                    <div aria-hidden className={`absolute -bottom-5 right-10 ${classes.floatFast}`}>
                        <span
                            className='flex items-center gap-1.5 rounded-lg bg-[#0d2154] px-3.5 py-2 text-xs font-bold text-white shadow-elevation2
                                ring-1 ring-white/15'>
                            <Location size={16} color='#FBBF24' />
                            سرویس در محل
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
